import { IconClose } from '@s4l/icons/IconClose'
import { BreakpointSizes, useBrowser } from '@s4l/utils/hooks/useBrowser'
import { isRTL } from '@s4l/utils/library/rtl'
import cn from 'classnames'
import type { ReactNode } from 'react'
import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { Drawer } from 'vaul'
import styles from './styles.module.css'
import { Footer } from '../Footer'
import { useKeyboardReposition } from '../hooks/useKeyboardReposition'

export interface BottomSheetContentProps {
  className?: string
  children: ReactNode
  footer?: ReactNode
  title?: string | React.ReactNode
  titleIcon?: React.ReactElement
  modalOnDesktop?: boolean
  contentPadding?: boolean
  scroll?: boolean
  size?: 's' | 'm' | 'l'
  hiddenTitle?: boolean
  noClose?: boolean
  onAnimationStart?: React.AnimationEventHandler<HTMLDivElement>
}

export interface BottomSheetContentRef {
  scrollToTop: () => void
}

export const Content = forwardRef<BottomSheetContentRef, BottomSheetContentProps>(
  (
    {
      className,
      children,
      title,
      titleIcon,
      modalOnDesktop,
      contentPadding = true,
      scroll = false,
      size = 'm',
      hiddenTitle = false,
      noClose = false,
      footer,
      ...props
    },
    ref
  ) => {
    const { activeBreakpoint, isAppleDevice } = useBrowser()
    const isDesktop = activeBreakpoint >= BreakpointSizes.L
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const drawerRef = useKeyboardReposition(isAppleDevice)

    useImperativeHandle(ref, () => ({
      scrollToTop: () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        }
      },
    }))

    return (
      <Drawer.Portal>
        <Drawer.Overlay className={styles.overlay} data-testid="overlay" />
        <Drawer.Content
          ref={drawerRef}
          data-vaul-no-drag={(modalOnDesktop && isDesktop) || null}
          data-rtl={isRTL()}
          className={cn(styles.content, className, {
            [styles.contentModal]: modalOnDesktop,
            [styles.sizeSmall]: size === 's',
            [styles.sizeLarge]: size === 'l',
          })}
          {...props}
        >
          {title && (
            <Drawer.Title
              className={cn(styles.title, {
                [styles.hiddenTitle]: hiddenTitle,
              })}
              hidden
            >
              <span>
                {title}
                {titleIcon &&
                  React.cloneElement(titleIcon, {
                    width: titleIcon.props.width ?? 18,
                    height: titleIcon.props.height ?? 18,
                    fill: titleIcon.props.fill ?? 'currentColor',
                    viewBox: `0 0 ${titleIcon.props.width ?? 18} ${titleIcon.props.height ?? 18}`,
                  })}
              </span>

              {!noClose && (
                <Drawer.Close className={styles.close}>
                  <IconClose />
                </Drawer.Close>
              )}
            </Drawer.Title>
          )}
          <div
            ref={scrollContainerRef}
            className={cn({
              [styles.textContainer]: contentPadding,
              [styles.scrollContainer]: scroll,
            })}
          >
            {children}
          </div>
          {footer && <Footer>{footer}</Footer>}
        </Drawer.Content>
      </Drawer.Portal>
    )
  }
)

Content.displayName = 'BottomSheet.Content'
